import { fontAttrs } from "@/components/tools/printables/page-kit";
import type {
  BorderStyle,
  CertificateLayout,
  FontSet,
  PlacedLine,
} from "@/lib/tool-kit/printables/certificate";
import { r2 } from "@/lib/tool-kit/printables/page";
import {
  cornerArc,
  rosettePath,
  starPath,
} from "@/lib/tool-kit/printables/shapes";
import { fitSize, type TextMeasure } from "@/lib/tool-kit/printables/text";

const INK = "#222222";
const STAR_SPACING = 14;

function Stars({
  layout,
  accent,
}: {
  layout: CertificateLayout;
  accent: string;
}) {
  const { border } = layout;
  const inset = 6;
  const left = border.x + inset;
  const right = border.x + border.width - inset;
  const top = border.y + inset;
  const bottom = border.y + border.height - inset;
  const across = Math.max(1, Math.round((right - left) / STAR_SPACING));
  const down = Math.max(1, Math.round((bottom - top) / STAR_SPACING));
  const centres: [number, number][] = [];
  for (let i = 0; i <= across; i += 1) {
    const x = left + ((right - left) * i) / across;
    centres.push([x, top], [x, bottom]);
  }
  for (let j = 1; j < down; j += 1) {
    const y = top + ((bottom - top) * j) / down;
    centres.push([left, y], [right, y]);
  }
  return (
    <g fill={accent}>
      {centres.map(([x, y]) => (
        <path d={starPath(x, y, 2.4)} key={`${r2(x)}-${r2(y)}`} />
      ))}
    </g>
  );
}

function ClassicCorners({
  layout,
  accent,
}: {
  layout: CertificateLayout;
  accent: string;
}) {
  const { border } = layout;
  const inset = 2.6;
  const x1 = border.x + inset;
  const y1 = border.y + inset;
  const x2 = border.x + border.width - inset;
  const y2 = border.y + border.height - inset;
  const radius = 9;
  return (
    <g fill="none" stroke={accent} strokeWidth={0.5}>
      <rect height={r2(y2 - y1)} width={r2(x2 - x1)} x={r2(x1)} y={r2(y1)} />
      <path d={cornerArc(x1 + 3, y1 + 3, radius, "tl")} />
      <path d={cornerArc(x2 - 3, y1 + 3, radius, "tr")} />
      <path d={cornerArc(x1 + 3, y2 - 3, radius, "bl")} />
      <path d={cornerArc(x2 - 3, y2 - 3, radius, "br")} />
    </g>
  );
}

/** The frame around the certificate. */
export function CertificateBorder({
  layout,
  style,
  accent,
}: {
  layout: CertificateLayout;
  style: BorderStyle;
  accent: string;
}) {
  if (style === "none") {
    return null;
  }
  const { border } = layout;
  const outerWidth = style === "simple" ? 1 : 1.6;
  return (
    <g>
      <rect
        fill="none"
        height={r2(border.height)}
        stroke={accent}
        strokeWidth={outerWidth}
        width={r2(border.width)}
        x={r2(border.x)}
        y={r2(border.y)}
      />
      {style === "double" ? (
        <rect
          fill="none"
          height={r2(border.height - 5)}
          stroke={accent}
          strokeWidth={0.4}
          width={r2(border.width - 5)}
          x={r2(border.x + 2.5)}
          y={r2(border.y + 2.5)}
        />
      ) : null}
      {style === "classic" ? (
        <ClassicCorners accent={accent} layout={layout} />
      ) : null}
      {style === "stars" ? <Stars accent={accent} layout={layout} /> : null}
    </g>
  );
}

/** A rosette seal with a star in the middle. */
export function CertificateSeal({
  cx,
  cy,
  radius,
  accent,
}: {
  cx: number;
  cy: number;
  radius: number;
  accent: string;
}) {
  return (
    <g>
      <path d={rosettePath(cx, cy, radius, 28, 0.92)} fill={accent} />
      <circle
        cx={cx}
        cy={cy}
        fill="none"
        r={r2(radius * 0.76)}
        stroke="#ffffff"
        strokeWidth={0.5}
      />
      <path d={starPath(cx, cy, radius * 0.5, radius * 0.2)} fill="#ffffff" />
    </g>
  );
}

function Line({
  line,
  spec,
  fill,
  anchor = "middle",
  letterSpacing,
}: {
  line: PlacedLine;
  spec: FontSet[keyof FontSet];
  fill: string;
  anchor?: "start" | "middle" | "end";
  letterSpacing?: number;
}) {
  return (
    <text
      fill={fill}
      fontSize={line.size}
      letterSpacing={letterSpacing}
      textAnchor={anchor}
      x={line.x}
      y={line.y}
      {...fontAttrs(spec)}
    >
      {line.text}
    </text>
  );
}

/** One certificate: border, text, the recipient's name, signature lines and seal. */
export function CertificateSheet({
  layout,
  fonts,
  accent,
  border,
  name,
  measure,
}: {
  layout: CertificateLayout;
  fonts: FontSet;
  accent: string;
  border: BorderStyle;
  name: string;
  measure: TextMeasure;
}) {
  const nameSize = name
    ? fitSize(
        measure,
        name,
        fonts.name,
        layout.nameWidth,
        layout.nameSizeMax,
        6
      )
    : 0;
  return (
    <g>
      <CertificateBorder accent={accent} layout={layout} style={border} />
      {layout.organisation ? (
        <Line
          fill={INK}
          letterSpacing={0.8}
          line={layout.organisation}
          spec={fonts.label}
        />
      ) : null}
      {layout.title.map((line) => (
        <Line fill={accent} key={line.y} line={line} spec={fonts.title} />
      ))}
      {layout.intro ? (
        <Line fill={INK} line={layout.intro} spec={fonts.intro} />
      ) : null}
      {name ? (
        <text
          fill={INK}
          fontSize={r2(nameSize)}
          textAnchor="middle"
          x={layout.centerX}
          y={layout.nameBaseline}
          {...fontAttrs(fonts.name)}
        >
          {name}
        </text>
      ) : null}
      <line
        stroke={INK}
        strokeWidth={0.3}
        x1={layout.nameRule.x1}
        x2={layout.nameRule.x2}
        y1={layout.nameRule.y}
        y2={layout.nameRule.y}
      />
      {layout.body.map((line) => (
        <Line fill={INK} key={line.y} line={line} spec={fonts.body} />
      ))}
      {[layout.dateRule, layout.signatureRule].map((rule) => (
        <line
          key={rule.x1}
          stroke={INK}
          strokeWidth={0.3}
          x1={rule.x1}
          x2={rule.x2}
          y1={rule.y}
          y2={rule.y}
        />
      ))}
      {layout.dateText ? (
        <Line fill={INK} line={layout.dateText} spec={fonts.label} />
      ) : null}
      {layout.dateLabel ? (
        <Line fill="#555555" line={layout.dateLabel} spec={fonts.label} />
      ) : null}
      {layout.signatureLabel ? (
        <Line fill="#555555" line={layout.signatureLabel} spec={fonts.label} />
      ) : null}
      {layout.seal ? (
        <CertificateSeal accent={accent} {...layout.seal} />
      ) : null}
    </g>
  );
}
